/**
 * D9: Cycle Decision Screen
 * 
 * Blocking screen shown when an evaluation cycle has expired.
 * Admin MUST choose one of 3 options before accessing the dashboard.
 * Candidate evaluations remain unrestricted.
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  AlertTriangle,
  Clock,
  ArrowRight,
  X,
  RefreshCw,
  Plus,
  Archive,
  Users,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import logger from '../utils/logger';

const TEXTS = {
  es: {
    title: 'Tu ciclo de evaluación ha vencido',
    subtitle: 'Debes tomar una decisión antes de continuar operando en la plataforma.',
    cycleInfo: 'Ciclo',
    expiredOn: 'Venció el',
    pendingEmployees: 'empleados sin evaluar',
    activePlans: 'planes de acción activos',
    
    extendTitle: 'Extender Ciclo',
    extendDesc: 'Las evaluaciones de empleados se reabren. El perfil de empresa no cambia.',
    extendUseCase: 'Cuando hay planes en progreso críticos o empleados que aún no evaluaron.',
    extendDaysLabel: 'Días de extensión (máx. 90)',
    extendButton: 'Extender Ciclo',
    
    closeNewTitle: 'Cerrar e Iniciar Nuevo',
    closeNewDesc: 'Cierra el ciclo actual (congela datos) e inicia la configuración de un nuevo ciclo.',
    closeNewUseCase: 'Cuando el ciclo está completo y quieres comenzar el siguiente período.',
    closeNewButton: 'Cerrar e Iniciar Nuevo',
    
    closeOnlyTitle: 'Solo Cerrar',
    closeOnlyDesc: 'Cierra el ciclo sin abrir uno nuevo. Las evaluaciones quedan bloqueadas hasta crear otro ciclo.',
    closeOnlyUseCase: 'Cuando quieres cerrar pero no estás listo para el siguiente ciclo.',
    closeOnlyButton: 'Solo Cerrar',
    
    processing: 'Procesando...',
    success: 'Decisión aplicada exitosamente',
    error: 'Error al procesar la decisión'
  },
  en: {
    title: 'Your evaluation cycle has expired',
    subtitle: 'You must make a decision before continuing to use the platform.',
    cycleInfo: 'Cycle',
    expiredOn: 'Expired on',
    pendingEmployees: 'employees not evaluated',
    activePlans: 'active action plans',
    
    extendTitle: 'Extend Cycle',
    extendDesc: 'Employee evaluations reopen. The company profile does not change.',
    extendUseCase: 'When there are critical plans in progress or employees who haven\'t evaluated yet.',
    extendDaysLabel: 'Extension days (max 90)',
    extendButton: 'Extend Cycle',
    
    closeNewTitle: 'Close & Start New',
    closeNewDesc: 'Closes the current cycle (freezes data) and immediately opens the new cycle wizard.',
    closeNewUseCase: 'When the cycle is complete and you want to start the next period.',
    closeNewButton: 'Close & Start New',
    
    closeOnlyTitle: 'Close Only',
    closeOnlyDesc: 'Closes the cycle without opening a new one. Evaluations are locked until a new cycle is created.',
    closeOnlyUseCase: 'When you want to close but aren\'t ready for the next cycle.',
    closeOnlyButton: 'Close Only',
    
    processing: 'Processing...',
    success: 'Decision applied successfully',
    error: 'Error processing decision'
  }
};

const CycleDecisionScreen = ({ cycleData, onDecisionMade }) => {
  const { language } = useLanguage();
  const t = TEXTS[language] || TEXTS.es;
  const [selectedOption, setSelectedOption] = useState(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [loading, setLoading] = useState(false);

  const handleDecision = async (decision) => {
    setLoading(true);
    try {
      const payload = { decision };
      if (decision === 'EXTEND') {
        payload.extension_days = extensionDays;
      }
      
      const response = await axios.post(
        `/v5/ciclos/${cycleData.ciclo_id}/decision`,
        payload
      );
      
      if (response.data?.success) {
        toast.success(t.success);
        onDecisionMade(response.data.data);
      }
    } catch (err) {
      toast.error(t.error);
      logger.error('Cycle decision error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div
      data-testid="cycle-decision-screen"
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)' }}
    >
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
          <h1 data-testid="decision-title" className="text-3xl font-bold text-white mb-2">
            {t.title}
          </h1>
          <p className="text-slate-400 text-lg">{t.subtitle}</p>
        </div>

        {/* Cycle Info */}
        <div className="flex items-center justify-center gap-6 mb-8 text-sm">
          <div className="flex items-center gap-2 text-slate-300">
            <Clock className="w-4 h-4 text-amber-400" />
            <span>{t.cycleInfo}: <strong className="text-white">{cycleData.nombre}</strong></span>
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <span>{t.expiredOn}: <strong className="text-amber-400">{formatDate(cycleData.fecha_vencimiento)}</strong></span>
          </div>
          {cycleData.pending_employees > 0 && (
            <Badge variant="outline" className="border-red-500/50 text-red-400">
              <Users className="w-3 h-3 mr-1" />
              {cycleData.pending_employees} {t.pendingEmployees}
            </Badge>
          )}
          {cycleData.active_action_plans > 0 && (
            <Badge variant="outline" className="border-blue-500/50 text-blue-400">
              <ClipboardList className="w-3 h-3 mr-1" />
              {cycleData.active_action_plans} {t.activePlans}
            </Badge>
          )}
        </div>

        {/* Three Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* EXTEND */}
          <Card
            data-testid="option-extend"
            className={`cursor-pointer transition-all duration-200 border-2 ${
              selectedOption === 'EXTEND'
                ? 'border-blue-500 bg-blue-950/50 shadow-lg shadow-blue-500/20'
                : 'border-slate-700 bg-slate-800/80 hover:border-slate-600'
            }`}
            onClick={() => setSelectedOption('EXTEND')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <CardTitle className="text-lg text-white">{t.extendTitle}</CardTitle>
              </div>
              <CardDescription className="text-slate-400">{t.extendDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 italic mb-4">{t.extendUseCase}</p>
              {selectedOption === 'EXTEND' && (
                <div className="space-y-2">
                  <Label className="text-slate-300 text-sm">{t.extendDaysLabel}</Label>
                  <Input
                    data-testid="extension-days-input"
                    type="number"
                    min={1}
                    max={90}
                    value={extensionDays}
                    onChange={(e) => setExtensionDays(Math.min(90, Math.max(1, parseInt(e.target.value) || 1)))}
                    className="bg-slate-900 border-slate-600 text-white"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button
                    data-testid="confirm-extend"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={(e) => { e.stopPropagation(); handleDecision('EXTEND'); }}
                    disabled={loading}
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Clock className="w-4 h-4 mr-2" />}
                    {loading ? t.processing : t.extendButton}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* CLOSE & START NEW */}
          <Card
            data-testid="option-close-new"
            className={`cursor-pointer transition-all duration-200 border-2 ${
              selectedOption === 'CLOSE_AND_NEW'
                ? 'border-emerald-500 bg-emerald-950/50 shadow-lg shadow-emerald-500/20'
                : 'border-slate-700 bg-slate-800/80 hover:border-slate-600'
            }`}
            onClick={() => setSelectedOption('CLOSE_AND_NEW')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <CardTitle className="text-lg text-white">{t.closeNewTitle}</CardTitle>
              </div>
              <CardDescription className="text-slate-400">{t.closeNewDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 italic mb-4">{t.closeNewUseCase}</p>
              {selectedOption === 'CLOSE_AND_NEW' && (
                <Button
                  data-testid="confirm-close-new"
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={(e) => { e.stopPropagation(); handleDecision('CLOSE_AND_NEW'); }}
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  {loading ? t.processing : t.closeNewButton}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* CLOSE ONLY */}
          <Card
            data-testid="option-close-only"
            className={`cursor-pointer transition-all duration-200 border-2 ${
              selectedOption === 'CLOSE_ONLY'
                ? 'border-slate-400 bg-slate-750/50 shadow-lg shadow-slate-500/20'
                : 'border-slate-700 bg-slate-800/80 hover:border-slate-600'
            }`}
            onClick={() => setSelectedOption('CLOSE_ONLY')}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full bg-slate-400" />
                <CardTitle className="text-lg text-white">{t.closeOnlyTitle}</CardTitle>
              </div>
              <CardDescription className="text-slate-400">{t.closeOnlyDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 italic mb-4">{t.closeOnlyUseCase}</p>
              {selectedOption === 'CLOSE_ONLY' && (
                <Button
                  data-testid="confirm-close-only"
                  className="w-full bg-slate-600 hover:bg-slate-700 text-white"
                  onClick={(e) => { e.stopPropagation(); handleDecision('CLOSE_ONLY'); }}
                  disabled={loading}
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Archive className="w-4 h-4 mr-2" />}
                  {loading ? t.processing : t.closeOnlyButton}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CycleDecisionScreen;
